import gsap from 'gsap';

const settings = {
    classNames: {
        active: 'accordion--active',
        closing: 'accordion--closing',
    },

    selectors: {
        container: '.accordion',
        items: '.accordion__items > div',
        button: '.accordion__title',
        content: '.accordion__content',
    },

    cssProperties: {
        slideAmount: '--slide-amount',
    },

    animation: {
        slowMoScale: 4,
        openDuration: 0.5,
        closeDuration: 0.5,
    },
};

export default class Accordion {
    constructor(
        container,
    ) {
        this.container = container;

        // Get references to our DOM elements
        const itemContainers = this.container
            .querySelectorAll(settings.selectors.items);

        // Setup accordion items
        this.items = [];
        for (const itemContainer of itemContainers) {
            const item = new AccordionItem(
                itemContainer,
            );

            this.items.push(item);
        }
    }

    static init() {
        const elements = document
            .querySelectorAll(settings.selectors.container);

        for (const element of elements) {
            new Accordion(element);
        }
    }
}

class AccordionItem {
    constructor(
        container,
    ) {
        this.container = container;
        this.isOpen = false;

        // Get references to our DOM elements
        this.button = this.container
            .querySelector(settings.selectors.button);
        this.content = this.container
            .querySelector(settings.selectors.content);
        if (!this.button || !this.content) {
            throw new Error();
        }

        // Setup open/close button
        this.button.addEventListener('click', (event) => {
            if (this.isOpen) {
                this.close(false, event.shiftKey);
            } else {
                this.open(false, event.shiftKey);
            }
        });
    }

    open(
        immediate,
        slowMo = false,
    ) {
        if (this.isOpen) {
            return;
        }

        this.isOpen = true;

        // Stop any ongoing animation
        if (this.openCloseTimeline) {
            this.openCloseTimeline.progress(1);
        }

        // Add our 'open' class
        this.container.classList.add(settings.classNames.active);

        if (immediate) {
            // Stop here
            return;
        }

        // Animate transition
        let duration = settings.animation.openDuration;
        if (slowMo) {
            duration *= settings.animation.slowMoScale;
        }

        this.openCloseTimeline = gsap.timeline();

        // Open the item
        this.openCloseTimeline.from(
            this.content,
            {
                duration: duration,
                ease: 'power2.out',
                height: 0,
            },
            0,
        );

        // Slide the inner content
        const progress = {
            percentComplete: 0,
        };

        this.openCloseTimeline.to(
            progress,
            {
                duration: duration,
                ease: 'power2.out',
                percentComplete: 1,
                onUpdate: () => {
                    const slideAmount = Math.round(
                        (1 - progress.percentComplete) * 1000,
                    ) / 1000;

                    this.content.style.setProperty(
                        settings.cssProperties.slideAmount,
                        slideAmount,
                    );
                },
            },
            0,
        );

        // Clean up
        this.openCloseTimeline.add(() => {
            // Clean up
            this.openCloseTimeline = null;

            this.content.removeAttribute('style');
        });
    }

    close(
        immediate,
        slowMo = false,
    ) {
        if (!this.isOpen) {
            return;
        }

        this.isOpen = false;

        // Stop any ongoing animation
        if (this.openCloseTimeline) {
            this.openCloseTimeline.progress(1);
        }

        if (immediate) {
            this.container.classList.remove(settings.classNames.active);

            // Stop here
            return;
        }

        // Add our 'closing' class and remove our 'open' class
        this.container.classList.add(settings.classNames.closing);
        this.container.classList.remove(settings.classNames.active);

        // Animate transition
        let duration = settings.animation.closeDuration;
        if (slowMo) {
            duration *= settings.animation.slowMoScale;
        }

        this.openCloseTimeline = gsap.timeline();

        // Close the item
        this.openCloseTimeline.to(
            this.content,
            {
                duration: duration,
                ease: 'power2.out',
                height: 0,
            },
            0,
        );

        // Slide the inner content
        const progress = {
            percentComplete: 0,
        };

        this.openCloseTimeline.to(
            progress,
            {
                duration: duration,
                ease: 'power2.out',
                percentComplete: 1,
                onUpdate: () => {
                    const slideAmount = Math.round(
                        progress.percentComplete * 1000,
                    ) / 1000;

                    this.content.style.setProperty(
                        settings.cssProperties.slideAmount,
                        slideAmount,
                    );
                },
            },
            0,
        );

        // Clean up
        this.openCloseTimeline.add(() => {
            // Clean up
            this.openCloseTimeline = null;

            this.container.classList.remove(settings.classNames.closing);
            this.content.removeAttribute('style');
        });
    }
}
